import React, { useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getTaskFilterOptions, getProjectFilterOptions, getEpicFilterOptions, getSmapFilterOptions, getProjectLevelFilterOptions } from '../../../constants/integrationTypes';
import {ENTITY} from "../../../redux/slices/timeline";

interface StatusFilterProps {
  statusFilters: string[];
  setStatusFilters: (filters: string[]) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  filterType?: 'task' | 'project' | 'smap';
  statusCounts?: Record<string, number>;
}

const StatusFilter: React.FC<StatusFilterProps> = ({
  statusFilters,
  setStatusFilters,
  isOpen,
  setIsOpen,
  filterType = 'project',
  statusCounts
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    singleRoadmapRes,
    integration_data: dataApiIntegrationData,
  } = useSelector((state: any) => state.roadmap);

  const {
    currentEntity
  } = useSelector((state: any) => state.timeline);

  const integrationType = useMemo(() => {
    if(currentEntity === ENTITY.ROADMAP) {
      return singleRoadmapRes?.integration_data?.integration_type;
    } else if(currentEntity === ENTITY.PROJECT) {
      return dataApiIntegrationData?.integration_type;
    }

    return null
  }, [currentEntity, singleRoadmapRes, dataApiIntegrationData])

  const statusOptions = useMemo(() => {
    if (filterType === 'task') {
      return getTaskFilterOptions();
    }
    if (filterType === 'smap') {
      return getSmapFilterOptions();
    }
    return getProjectLevelFilterOptions();
  }, [filterType]);

  const statusOptionsWithCounts = useMemo(() => {
    if (!statusCounts) return statusOptions;
    return statusOptions.map((opt: { value: string; label: string }) => {
      const countSuffix = opt.value !== '' ? ` (${statusCounts[opt.value] || 0})` : '';
      return {
        ...opt,
        label: opt.label + countSuffix
      };
    });
  }, [statusOptions, statusCounts]);

  useEffect(() => {
    if (!isOpen) return;
    function handleClick(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, setIsOpen]);

  const toggleStatus = (status: string) => {
    setStatusFilters(
      statusFilters.includes(status)
        ? statusFilters.filter(s => s !== status)
        : [...statusFilters, status]
    );
  };

  return (
    <div className="relative flex items-center" ref={dropdownRef}>
      <button
        id="dropdownCheckboxButton"
        type="button"
        className="text-light-text-secondary dark:text-gray-200 bg-transparent font-medium rounded-lg text-xs px-3 pr-2 py-2.5 text-center inline-flex items-center hover:text-light-text-primary dark:hover:text-white hover:bg-light-interactive-hover dark:hover:bg-slate-700 transition-colors duration-200"
        onClick={() => setIsOpen(!isOpen)}
      >
        {(() => {
          if (statusFilters.length === 0) return 'Status';
          if (statusFilters.length === 1) {
            const opt = statusOptionsWithCounts.find((o: { value: string; label: string }) => o.value === statusFilters[0]);
            return opt?.label || 'Status';
          }
          return `${statusFilters.length} selected`;
        })()}
        <svg className={`w-2.5 h-2.5 ms-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 4 4 4-4"/>
        </svg>
      </button>
      {isOpen && (
        <div className="z-10 absolute top-full left-0 mt-2 w-40 bg-light-surfacePrimary dark:bg-secondary-dark border border-light-border-default dark:border-gray-600 rounded-lg shadow-lg overflow-hidden">
          <ul className="p-3 space-y-3 text-sm text-light-text-primary dark:text-gray-200">
            {statusOptionsWithCounts.slice(1).map((opt: { value: string; label: string }) => (
              <li key={opt.value}>
                <div className="flex items-center">
                  <input
                    id={`checkbox-item-${opt.value}`}
                    type="checkbox"
                    checked={statusFilters.includes(opt.value)}
                    onChange={() => toggleStatus(opt.value)}
                    className="w-3 h-3 text-primary-light bg-light-input-bg border-light-input-border rounded focus:ring-primary-light focus:ring-2 dark:bg-gray-600 dark:border-gray-500 dark:focus:ring-blue-600 dark:ring-offset-gray-700 transition-colors duration-200"
                  />
                  <label
                    htmlFor={`checkbox-item-${opt.value}`}
                    className="ms-2 text-xs font-medium text-light-text-primary dark:text-gray-300 cursor-pointer hover:text-light-text-secondary transition-colors duration-200"
                  >
                    {opt.label}
                  </label>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default StatusFilter;
